import React, { useState, useEffect, createContext, useContext } from 'react';
import { AuthUser, LoginResult, getCurrentSession, login as authLogin, logout as authLogout } from '../services/auth';
import { logLogin } from './useActivityLog';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useAuthProvider(): AuthContextType {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCurrentSession().then((session) => {
      setUser(session);
      setLoading(false);
    });
  }, []);

  // Both ways in are logged, and only here: a sign-in through the "choose a new
  // password" branch is still a sign-in, and logging it at the caller would have
  // missed it. Fire-and-forget — a failure to log must never block a login.
  const login = async (email: string, password: string): Promise<LoginResult> => {
    const result = await authLogin(email, password);
    if (result.type === 'success') {
      setUser(result.user);
      logLogin();
      return result;
    }
    return {
      type: 'newPasswordRequired',
      complete: async (newPassword: string) => {
        const u = await result.complete(newPassword);
        setUser(u);
        logLogin();
        return u;
      },
    };
  };

  const logout = () => {
    authLogout();
    setUser(null);
  };

  return { user, loading, login, logout };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const value = useAuthProvider();
  return React.createElement(AuthContext.Provider, { value }, children);
}
